<template lang="pug">
.pulse365-container
  .pulse365-header
    .header-left
      h2 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.page_title') }}
      aiq-tooltip(
        v-if="excludesNoInteraction"
        placement="top"
        :content="$t('settings_tab.agent_tab.dashboard.pulse_configuration.no_interaction.excluded_hint')"
      )
        span.no-interaction-badge
          | {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.no_interaction.excluded_badge') }}
    .header-right
      .date-picker-wrapper
        aiq-date-picker(
          v-model="dateRange"
          type="daterange"
          :shortcuts="datePickerOptions.shortcuts"
          :disabled-date="datePickerOptions.disabledDate"
          format="YYYY/MM/DD"
          :clearable="false"
          popper-class="metrics-time-range__date-picker-popup"
          :teleported="false"
          :default-time="getDatePickerDefaultTime()"
          @change="onDateRangeChange"
        )

  Pulse365Grid(
    v-if="pulseConfig"
    :config="pulseConfig"
    :metricsData="metricsData"
    :loading="isLoading"
  )
</template>

<script>
import { mapState } from 'vuex';
import get from 'lodash/get';
import dayjs from '@/localPlugins/dayjs';
import { useTimeZone, getDatePickerDefaultTime, unixStampToTzAttachedMoment } from '@/libs';
import { dstDateRangeUtc, createPickerDateRangeOption, getLastQuarterRange } from '@/pages/Metrics/configs/dateRange';
import Pulse365Grid from './components/Pulse365Grid.vue';
import { DEFAULT_COST_CONFIG } from './constants';

const MAX_DATE_RANGE_MONTHS = 12;

export default {
  name: 'Pulse365',
  components: {
    Pulse365Grid,
  },
  data() {
    return {
      dateRange: [
        dayjs().subtract(1, 'month').startOf('day').toDate(),
        dayjs().subtract(1, 'day').endOf('day').toDate(),
      ],
      // Starts true: mounted() awaits the timezone and configuration lookups
      // before fetching, and without this the empty state renders in that gap.
      isLoading: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  computed: {
    ...mapState({
      pulseConfig: state => state.settings.pulseConfig,
      metricsData: state => state.pulse.pulse365.data,
    }),
    datePickerOptions() {
      const todayStartInTz = dayjs().tz(this.timezone).startOf('day').valueOf();
      const twelveMonthsAgo = dayjs().tz(this.timezone).subtract(MAX_DATE_RANGE_MONTHS, 'months').startOf('day').valueOf();
      return {
        disabledDate: (time) => time.getTime() >= todayStartInTz || time.getTime() < twelveMonthsAgo,
        shortcuts: [
          { label: 'Last Month', startTime: () => dayjs().subtract(1, 'month').startOf('month'), endTime: () => dayjs().subtract(1, 'month').endOf('month') },
          { label: 'Last Quarter', ...getLastQuarterRange() },
        ].map(createPickerDateRangeOption),
      };
    },
    // Reads the org setting the page already loaded, rather than introducing a
    // second source of truth for what the endpoint was told to do.
    excludesNoInteraction() {
      return get(this.pulseConfig, 'include_no_interaction') === false;
    },
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    await this.loadPulseConfig();
    await this.fetchMetrics();
  },
  methods: {
    async loadPulseConfig() {
      try {
        const config = await this.$store.dispatch('settings/getSetting', {
          name: 'pulse_configuration',
          stateName: 'pulseConfig',
        });

        if (config && !config.cost_config) {
          const updatedConfig = { ...config, cost_config: { ...DEFAULT_COST_CONFIG } };
          await this.$store.dispatch('settings/updateSetting', {
            name: 'pulse_configuration',
            payload: updatedConfig,
            stateName: 'pulseConfig',
          });
        }
      } catch (err) {
        this.$aiq.notify.error('Failed to load pulse configuration');
      }
    },
    getDatePickerDefaultTime,
    async fetchMetrics() {
      if (!this.pulseConfig) return;

      this.isLoading = true;
      try {
        const [rawStart, rawEnd] = dstDateRangeUtc(this.dateRange);
        const params = {
          period_start: unixStampToTzAttachedMoment(rawStart, this.timezone).unix(),
          period_end: unixStampToTzAttachedMoment(rawEnd, this.timezone).unix(),
          time_zone: this.timezone,
        };

        await this.$store.dispatch('pulse/getPulse365Metrics', params);
      } catch (err) {
        this.$aiq.notify.error('Failed to fetch metrics');
      } finally {
        this.isLoading = false;
      }
    },
    onDateRangeChange() {
      return this.fetchMetrics();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/aiq-variables.scss";

.pulse365-container {
  padding: 16px 20px;
  height: calc(100vh - 160px);
  overflow-y: auto;
  background: #f5f7fa;
}

.pulse365-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: $aiq-color-text-primary;
    margin: 0;
  }
}

// Mirrors .applied-filter-chip in AdvancedConversationFilter.vue, the existing
// treatment for "this filter is on", so the two read as the same idea.
.no-interaction-badge {
  padding: 4px 8px;
  border-radius: 4px;
  background-color: $aiq-bgc-conversations-filter;
  color: $aiq-text-counters-filter;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  cursor: default;
}

.date-picker-wrapper {
  min-width: 260px;
  position: relative;
}
</style>

<style lang="scss">
@import "../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .pulse365-container {
  background: $aiq-bgc-body;

  .pulse365-header h2 {
    color: $aiq-color-black;
  }

  .no-interaction-badge {
    background-color: $aiq-bgc-conversations-filter;
    color: $aiq-text-counters-filter;
  }
}
</style>
